import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import Draggable from 'react-draggable';

import Box from '../box/box.jsx';

import styles from './floating-panel.css';

const FloatingPanel = ({
    bodyClassName,
    children,
    className,
    defaultPosition,
    defaultSize,
    headerClassName,
    headerLabel,
    minSize,
    onStop,
    onResize,
    onResizeStop,
    style
}) => {
    const panelRef = React.useRef(null);
    const resizeStateRef = React.useRef(null);
    const clampSize = React.useCallback(size => {
        if (!size) return null;
        return {
            width: Math.max(minSize.width, size.width),
            height: Math.max(minSize.height, size.height)
        };
    }, [minSize.height, minSize.width]);
    const [panelSize, setPanelSize] = React.useState(() => clampSize(defaultSize));
    const [isReady, setIsReady] = React.useState(false);

    React.useEffect(() => {
        let timerId = null;
        if (typeof requestAnimationFrame === 'function') {
            timerId = requestAnimationFrame(() => setIsReady(true));
            return () => cancelAnimationFrame(timerId);
        }
        timerId = setTimeout(() => setIsReady(true), 0);
        return () => clearTimeout(timerId);
    }, []);

    const beginResize = event => {
        event.preventDefault();
        event.stopPropagation();

        const panelEl = panelRef.current;
        if (!panelEl) return;

        const rect = panelEl.getBoundingClientRect();
        resizeStateRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            startWidth: rect.width,
            startHeight: rect.height
        };

        const handleMove = moveEvent => {
            const resizeState = resizeStateRef.current;
            if (!resizeState) return;

            const nextWidth = Math.max(
                minSize.width,
                resizeState.startWidth + (moveEvent.clientX - resizeState.startX)
            );
            const nextHeight = Math.max(
                minSize.height,
                resizeState.startHeight + (moveEvent.clientY - resizeState.startY)
            );

            setPanelSize({width: nextWidth, height: nextHeight});
            if (onResize) {
                onResize(Math.round(nextWidth), Math.round(nextHeight));
            }
        };

        const handleUp = upEvent => {
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);

            const resizeState = resizeStateRef.current;
            resizeStateRef.current = null;
            if (!resizeState) return;

            const nextWidth = Math.max(
                minSize.width,
                resizeState.startWidth + (upEvent.clientX - resizeState.startX)
            );
            const nextHeight = Math.max(
                minSize.height,
                resizeState.startHeight + (upEvent.clientY - resizeState.startY)
            );

            setPanelSize({width: nextWidth, height: nextHeight});
            if (onResize) {
                onResize(Math.round(nextWidth), Math.round(nextHeight));
            }
            if (onResizeStop) {
                onResizeStop(Math.round(nextWidth), Math.round(nextHeight));
            }
        };

        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleUp);
    };

    const mergedStyle = Object.assign({}, style, panelSize ? {
        width: `${panelSize.width}px`,
        height: `${panelSize.height}px`
    } : {}, {
        opacity: isReady ? 1 : 0,
        transition: 'opacity 120ms ease'
    });

    return (
        <Draggable
            bounds="parent"
            defaultClassNameDragging={styles.dragging}
            defaultPosition={defaultPosition}
            handle={`.${styles.handle}`}
            onStop={onStop}
        >
            <Box
                componentRef={panelRef}
                className={classNames(styles.panel, className)}
                style={mergedStyle}
            >
                <div className={classNames(styles.header, styles.handle, headerClassName)}>
                    <span className={styles.title}>{headerLabel}</span>
                </div>
                <div className={classNames(styles.body, bodyClassName)}>
                    {children}
                </div>
                <button
                    aria-label="Resize panel"
                    className={styles.resizeHandle}
                    type="button"
                    onMouseDown={beginResize}
                />
            </Box>
        </Draggable>
    );
};

FloatingPanel.propTypes = {
    bodyClassName: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    defaultPosition: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number
    }),
    defaultSize: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    }),
    headerClassName: PropTypes.string,
    headerLabel: PropTypes.node.isRequired,
    minSize: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    }),
    onStop: PropTypes.func,
    onResize: PropTypes.func,
    onResizeStop: PropTypes.func,
    style: PropTypes.object // eslint-disable-line react/forbid-prop-types
};

FloatingPanel.defaultProps = {
    minSize: {
        width: 320,
        height: 180
    }
};

export default FloatingPanel;
